import { Monitor, Moon, Sun, type LucideIcon } from 'lucide-react';
import { useTheme } from 'next-themes';

import {
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import {
  isAppearanceThemeId,
} from '@/lib/navigation/account-action-catalog';
import {
  AppearanceThemeId,
} from '@/lib/navigation/types/account-action-catalog-enums';
import {
  APPEARANCE_THEME_OPTIONS,
} from '@/lib/navigation/constants/account-action-catalog-constants';
import { cn } from '@/lib/utils';

const THEME_ICONS: Record<AppearanceThemeId, LucideIcon> = {
  [AppearanceThemeId.LIGHT]: Sun,
  [AppearanceThemeId.DARK]: Moon,
  [AppearanceThemeId.SYSTEM]: Monitor,
};

interface AppearanceThemeRadioGroupProps {
  itemClassName?: string;
  onThemeSelected?: () => void;
}

export function AppearanceThemeRadioGroup({
  itemClassName,
  onThemeSelected,
}: AppearanceThemeRadioGroupProps) {
  const { theme, setTheme } = useTheme();
  const selectedTheme = isAppearanceThemeId(theme ?? '')
    ? theme
    : AppearanceThemeId.SYSTEM;

  return (
    <DropdownMenuRadioGroup
      value={selectedTheme}
      onValueChange={value => {
        if (!isAppearanceThemeId(value)) {
          return;
        }
        setTheme(value);
        onThemeSelected?.();
      }}
    >
      {APPEARANCE_THEME_OPTIONS.map(option => {
        const Icon = THEME_ICONS[option.id];
        return (
          <DropdownMenuRadioItem
            key={option.id}
            value={option.id}
            className={cn('gap-2 px-2 py-1.5 type-body', itemClassName)}
          >
            <Icon className="size-4 shrink-0" />
            {option.label}
          </DropdownMenuRadioItem>
        );
      })}
    </DropdownMenuRadioGroup>
  );
}

interface MobileAppearanceThemeListProps {
  onThemeSelected?: () => void;
}

export function MobileAppearanceThemeList({
  onThemeSelected,
}: MobileAppearanceThemeListProps) {
  const { theme, setTheme } = useTheme();
  const selectedTheme = isAppearanceThemeId(theme ?? '')
    ? theme
    : AppearanceThemeId.SYSTEM;

  return (
    <ul className="mt-1 flex flex-col gap-1" role="radiogroup" aria-label="Appearance">
      {APPEARANCE_THEME_OPTIONS.map(option => {
        const Icon = THEME_ICONS[option.id];
        const isChecked = selectedTheme === option.id;
        return (
          <li key={option.id}>
            <button
              aria-checked={isChecked}
              className={cn(
                'flex h-12 w-full items-center gap-3 rounded-control px-3 text-left text-sm font-medium text-content-primary transition-[color,background-color] duration-fast',
                'hover:bg-neutral-interaction-hover',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-ring',
                isChecked && 'bg-neutral-interaction-selected'
              )}
              onClick={() => {
                setTheme(option.id);
                onThemeSelected?.();
              }}
              role="radio"
              type="button"
            >
              <Icon className="size-4 shrink-0 text-content-tertiary" />
              <span className="flex-1">{option.label}</span>
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-4 items-center justify-center rounded-full border border-border-control',
                  isChecked && 'border-interaction-selected-indicator'
                )}
              >
                {isChecked ? (
                  <span className="size-1.5 rounded-full bg-interaction-selected-indicator" />
                ) : null}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
